/**
 * App Mode Manager - Single Source of Truth for Application Mode
 * 
 * Provides a centralized, cached way to determine the current app mode
 * (explore, map, favorites, search, area) without repeatedly parsing the URL.
 * 
 * Benefits:
 * - Single source of truth (no scattered mode checks)
 * - Cached parsing (only re-parses when URL changes)
 * - Consistent API across the app
 * - Emits events when mode changes
 */

import { parseUrlParams } from './utils/url-utils.js';

// Cached mode data
let cachedModeData = null;
let currentMode = null;

// Event listeners for mode changes
const modeChangeListeners = new Set();

/**
 * Gets the current app mode with cached parsing
 * @returns {Object} { mode, areaSlug, params }
 */
export function getCurrentAppMode() {
    const currentUrl = window.location.pathname + window.location.search;

    // Re-parse only if URL changed
    if (!cachedModeData || cachedModeData.url !== currentUrl) {
        const parsed = parseUrlParams();
        const previousMode = currentMode;
        currentMode = parsed.mode || 'area'; // Default to 'area' if no mode

        cachedModeData = {
            mode: currentMode,
            areaSlug: parsed.areaSlug,
            params: parsed.params || [],
            url: currentUrl
        };

        // Emit mode change event if mode actually changed
        if (previousMode !== currentMode) {
            emitModeChange(currentMode, previousMode);
        }
    }

    return {
        mode: cachedModeData.mode,
        areaSlug: cachedModeData.areaSlug,
        params: cachedModeData.params
    };
}

/**
 * Gets just the mode string (convenience function)
 * @returns {string} Current mode ('explore' | 'map' | 'favorites' | 'search' | 'area')
 */
export function getCurrentMode() {
    return getCurrentAppMode().mode;
}

/**
 * Checks if currently in a specific mode
 * @param {string} mode - Mode to check
 * @returns {boolean}
 */
export function isMode(mode) {
    return getCurrentMode() === mode;
}

/**
 * Checks if currently in favorites mode
 * @returns {boolean}
 */
export function isFavoritesMode() {
    const mode = getCurrentMode();
    return mode === 'favorites' || mode === 'myfavorites';
}

/**
 * Checks if currently in explore mode
 * @returns {boolean}
 */
export function isExploreMode() {
    return isMode('explore');
}

/**
 * Checks if currently in map mode
 * @returns {boolean}
 */
export function isMapMode() {
    return isMode('map') || isMode('discover');
}

/**
 * Checks if currently in search mode
 * @returns {boolean}
 */
export function isSearchMode() {
    return isMode('search');
}

/**
 * Checks if currently in area mode (default, no specific mode)
 * @returns {boolean}
 */
export function isAreaMode() {
    const mode = getCurrentMode();
    return !mode || mode === 'area' || (mode !== 'explore' && mode !== 'map' && mode !== 'favorites' && mode !== 'search');
}

/**
 * Registers a listener for mode changes
 * @param {Function} callback - Function to call when mode changes
 * @returns {Function} Unsubscribe function
 */
export function onModeChange(callback) {
    modeChangeListeners.add(callback);

    // Return unsubscribe function
    return () => {
        modeChangeListeners.delete(callback);
    };
}

/**
 * Emits mode change event to all listeners
 * @param {string} newMode - New mode
 * @param {string} previousMode - Previous mode
 */
function emitModeChange(newMode, previousMode) {
    const event = {
        mode: newMode,
        previousMode: previousMode,
        areaSlug: cachedModeData?.areaSlug,
        params: cachedModeData?.params || []
    };

    // Call all listeners
    modeChangeListeners.forEach(callback => {
        try {
            callback(event);
        } catch (error) {
            console.warn('[AppModeManager] Mode change listener error:', error);
        }
    });

    // Also dispatch DOM event for compatibility
    window.dispatchEvent(new CustomEvent('appModeChanged', { detail: event }));
}

/**
 * Clears the cached mode data (forces re-parsing on next call)
 * Useful for testing or when you know URL changed but pathname/search didn't
 */
export function clearModeCache() {
    cachedModeData = null;
    currentMode = null;
}

// Listen for browser navigation (back/forward buttons)
window.addEventListener('popstate', () => {
    clearModeCache();
});

// Listen for programmatic URL changes (pushState/replaceState)
// Override history methods to detect changes
const originalPushState = history.pushState;
const originalReplaceState = history.replaceState;

history.pushState = function (...args) {
    originalPushState.apply(history, args);
    clearModeCache();
};

history.replaceState = function (...args) {
    originalReplaceState.apply(history, args);
    clearModeCache();
};
